import './WebChat.css';
import './GroupMgmt.css';

import { IMessage, IUser, _dataProvider } from '../dataProvider/DataProvider';
import { ISearchBoxStyles, SearchBox } from '@fluentui/react/lib/SearchBox';
import React, { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { getJwtObj, legitHosts } from '../SharedCommon/utils';
import { processImageForUpload } from '../SharedCommon/image';
import { validateAndOpenUrl as validateAndOpenUrlUtil } from '../SharedCommon/url';

import AcceptContactCard from './AcceptContactCard';
import { Dialog } from '@fluentui/react/lib/Dialog';
import { chatSocket } from '../dataProvider/socket';
import { GiftedChat } from '../gifted/GiftedChat';
import { getIdigestChatHostConfig } from '../chatHost';
import Message from '../gifted/Message';
import { IPromptPickFilesInGroup } from './PickFilesInGroup';
import { UserProfileDialog } from '../SharedCommon/UserProfile';
import defaultAvatar from '../img/icon-DefaultProfilePicture.png';
import deleteIcon from '../img/icon-Delete2-white.png';
import i18n from '../i18n';
import replyIcon from '../img/icon-reply.png';
import myCollectionIcon from '../img/icon-mycollection-black.png';
import groupCollectionIcon from '../img/icon-collection-black.png';
import removeGroupCollectionIcon from '../img/icons/icon-remove-group-collection.png';
import removeMyCollectionIcon from '../img/icons/icon-remove-my-collection.png';
import forwardIcon from '../img/icons/icon-share.png';
import hideIcon from '../img/icon-Hide.png';
import selectIcon from '../img/icon-Select.png';
import searchActiveIcon from '../img/icon-Search-black.png';
import searchIcon from '../img/icon-Search-white.png';
import sendIcon from '../img/icon-Post.png';
import uploadIcon from '../img/icon-file.png';
import checkinIcon from '../img/icon-checkin.png';
import signupIcon from '../img/icon-signup.png';
import { userAvatarName } from '../SharedCommon/UserAvatarName';
import voiceIcon from '../img/icon-Voice-input.png';
import emojiIcon from '../img/icon-imoji.png';
import keyboardIcon from '../img/icon-keyboard.png';
import upIcon from '../img/icon-Up.png';
import useToast from '../SharedCommon/useToast';
import { useAudioPlayer } from './WebChat/hooks/useAudioPlayer';
import { useVoiceRecording } from './WebChat/hooks/useVoiceRecording';
import { UNREAD_INDICATOR_MIN_COUNT, useMessages } from './WebChat/hooks/useMessages';
import { useMessageFilters } from './WebChat/hooks/useMessageFilters';
import { useBatchSelection } from './WebChat/hooks/useBatchSelection';
import ConfirmDialog from './common/ConfirmDialog';
import { createIconPair } from './common/IconButton';
import { getSearchBoxStyles } from './shellStyles';
import { SearchBoxChrome } from './SearchBoxChrome';
import type { IdigestChatTheme } from '../theme';

const EmojiPickerWrapper = lazy(() => import('./WebChat/EmojiPickerWrapper'));

interface IWebChatProps {
  groupId: number;
  height: string;
  theme?: IdigestChatTheme;
  disappearingMessages?: number;
  launchOneOnOneWithFriend: any;
  discussionShareInGroup?: number;
  onDiscussionTagsLoaded?: (tags: number[]) => void;
  onBatchSelectModeChange?: (isBatchMode: boolean, selectedCount: number, hasOthersMessages?: boolean) => void;
  onGroupInfoUpdated?: (groupInfo: any) => void;
  onShareTargetSelectRequest?: (
    method: 'one-by-one' | 'package',
    selectedMessages: any[],
    sourceGroupId: number
  ) => void;
  onFilterStateChange?: (activeFilter: 'none' | 'user' | 'group' | 'file') => void;
  batchHandlersRef?: React.MutableRefObject<{
    onExit: () => void;
    onGroupCollection: () => void;
    onMyCollection: () => void;
    onDelete: () => void;
    // onShare: () => void; // 暂时隐藏转发功能
    onRemoveGroupCollection?: () => void;
    onRemoveMyCollection?: () => void;
    // onShareTargetSelect?: (method: 'one-by-one' | 'package') => void; // 暂时隐藏转发功能
  } | null>;
}

const theAllUserUser = {
  userId: -2,
  name: i18n.t('Chatroom.AllUsers'),
  displayName: i18n.t('Chatroom.AllUsers'),
  loginId: '',
  isGroupLeader: false
};

type FilterType = 'none' | 'file' | 'user' | 'group';

type GroupTaskCard = {
  kind: 'checkin' | 'checkinSuccess' | 'signup' | 'readingPlan';
  title: string;
  id?: string;
  description?: string;
  deadline?: number | string | null;
  scriptures?: string[];
  attendance?: Array<{ name: string; userId?: number }>;
  progress?: number;
  timeRemaining?: number | null;
  image?: string;
};

const groupTaskLabels: Record<GroupTaskCard['kind'], string> = {
  checkin: '打卡',
  checkinSuccess: '打卡',
  signup: '接龙',
  readingPlan: '读经计划学习通知'
};

const groupTaskIcons: Partial<Record<GroupTaskCard['kind'], string>> = {
  checkin: checkinIcon,
  checkinSuccess: checkinIcon,
  signup: signupIcon
};

const groupTaskIconText: Record<GroupTaskCard['kind'], string> = {
  checkin: '卡',
  checkinSuccess: '卡',
  signup: '接',
  readingPlan: '读'
};

const formatGroupTaskDeadline = (value: GroupTaskCard['deadline']): string => {
  if (!value) {
    return '';
  }
  if (typeof value === 'string') {
    return value;
  }
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) {
    return '';
  }
  const pad = (num: number) => String(num).padStart(2, '0');
  return `${date.getFullYear()}/${pad(date.getMonth() + 1)}/${pad(date.getDate())} ${pad(date.getHours())}:${pad(
    date.getMinutes()
  )}`;
};

const formatRemainingSeconds = (value: unknown): string => {
  const seconds = Number(value);
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return '';
  }
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (days > 0) {
    return `${days}天${hours > 0 ? `${hours}小时` : ''}`;
  }
  if (hours > 0) {
    return `${hours}小时${minutes > 0 ? `${minutes}分钟` : ''}`;
  }
  return `${Math.max(minutes, 1)}分钟`;
};

const getDisplayAttendance = (attendance: GroupTaskCard['attendance'] = []) => {
  if (attendance.length <= 11) {
    return attendance.map((item, index) => ({ ...item, displayIndex: index }));
  }
  return [
    ...attendance.slice(0, 10).map((item, index) => ({ ...item, displayIndex: index })),
    { name: '...', displayIndex: -1 },
    { ...attendance[attendance.length - 1], displayIndex: attendance.length - 1 }
  ];
};

const chineseEmojiKeywords = {
  笑: '😀😁😂🤣😊😄😆',
  哭: '😭😢😿',
  '爱心|愛心': '❤️💕💖💗💘💝💞💟',
  '赞|讚': '👍👏',
  '生气|生氣': '😠😡🤬',
  '亲吻|親吻': '😘😗😙😚💋',
  '惊讶|驚訝': '😮😯😲🤯',
  困: '😴😪🥱',
  火: '🔥',
  心碎: '💔',
  '开心|開心': '😊😃😄😁',
  '伤心|傷心': '😢😭☹️🙁',
  生病: '🤒🤕🤧',
  酷: '😎🕶️',
  害羞: '😳😊🤭',
  思考: '🤔💭',
  '无语|無語': '🙄😑',
  '尴尬|尷尬': '😅😓',

  手: '👋🤚🖐️✋👌✌️🤞🤟🤘🤙👈👉👆👇☝️👍👎👊✊🤛🤜👏🙌👐🤲🤝🙏',
  拜拜: '👋',
  握手: '🤝',
  '祈祷|祈禱': '🙏',
  肌肉: '💪',
  拍手: '👏',
  '举手|舉手': '🙋‍♀️🙋‍♂️',

  '猫|貓': '🐱🐈😺😸😹😻😼😽🙀😿😾',
  狗: '🐶🐕🦮🐕‍🦺',
  '熊猫|熊貓': '🐼',
  '独角兽|獨角獸': '🦄',
  青蛙: '🐸',
  '鸭子|鴨子': '🦆',
  '鸟|鳥': '🐦🕊️🦅🦉',
  蝴蝶: '🦋',
  蜜蜂: '🐝',
  '鱼|魚': '🐟🐠🐡',

  '苹果|蘋果': '🍎',
  香蕉: '🍌',
  '橙子|橙子': '🍊',
  草莓: '🍓',
  西瓜: '🍉',
  '汉堡|漢堡': '🍔',
  '披萨|披薩': '🍕',
  蛋糕: '🎂🍰🧁',
  咖啡: '☕',
  茶: '🍵🫖',
  啤酒: '🍺',
  酒: '🍷🥂🍾🍸🍹',
  '面条|麵條': '🍜🍝',
  '米饭|米飯': '🍚🍛',

  '车|車': '🚗🚕🚙🚌🚎',
  '飞机|飛機': '✈️🛩️',
  '火车|火車': '🚂🚃🚄🚅🚆🚇🚈🚉🚊🚝🚞',
  '自行车|自行車': '🚲🚴‍♀️🚴‍♂️',
  '摩托车|摩托車': '🏍️',
  船: '🚢⛵🛥️',
  '公交车|公車': '🚌',

  '太阳|太陽': '☀️🌞',
  月亮: '🌙🌛🌜🌝🌚',
  星星: '⭐🌟✨',
  '云|雲': '☁️⛅🌤️🌦️🌧️',
  雨: '🌧️☔⛈️',
  雪: '❄️☃️⛄🌨️',
  彩虹: '🌈',
  '闪电|閃電': '⚡🌩️',

  足球: '⚽',
  '篮球|籃球': '🏀',
  '网球|網球': '🎾',
  乒乓球: '🏓',
  游泳: '🏊‍♀️🏊‍♂️🏊',
  跑步: '🏃‍♀️🏃‍♂️🏃',

  '手机|手機': '📱',
  '电脑|電腦': '💻🖥️',
  '钱|錢': '💰💸💵💴💶💷🪙',
  '书|書': '📚📖📘📙📗📕',
  '音乐|音樂': '🎵🎶🎼🎤🎧',
  '相机|相機': '📷📸',
  '时钟|時鐘': '🕐🕑🕒🕓🕔🕕🕖🕗🕘🕙🕚🕛⏰⏱️⏲️',
  '钥匙|鑰匙': '🔑🗝️',

  '对|對': '✅✔️',
  '错|錯': '❌❎',
  '问号|問號': '❓❔',
  '感叹号|感嘆號': '❗❕',
  '箭头|箭頭': '⬆️⬇️⬅️➡️↗️↘️↙️↖️',
  '循环|循環': '🔄🔃🔁🔂',

  '庆祝|慶祝': '🎉🎊🥳',
  生日: '🎂🎈🎁',
  '圣诞|聖誕': '🎄🎅🤶🎁',
  新年: '🎆🎇✨',
  '万圣节|萬聖節': '🎃👻',

  '医生|醫生': '👨‍⚕️👩‍⚕️',
  '老师|老師': '👨‍🏫👩‍🏫',
  警察: '👮‍♀️👮‍♂️',
  '消防员|消防員': '👨‍🚒👩‍🚒',
  '厨师|廚師': '👨‍🍳👩‍🍳'
};

function searchChineseEmojis(searchTerm: string): string[] {
  if (!searchTerm || searchTerm.trim().length === 0) {
    return [];
  }

  const term = searchTerm.trim();
  const results = new Set<string>();

  Object.entries(chineseEmojiKeywords).forEach(([keywords, emojis]) => {
    const keywordList = keywords.split('|');

    const matched = keywordList.some((keyword) => keyword.includes(term) || term.includes(keyword));

    if (matched) {
      const emojiArray = Array.from(emojis);
      emojiArray.forEach((emoji) => results.add(emoji));
    }
  });

  return Array.from(results).slice(0, 20);
}

if (typeof window !== 'undefined') {
  (window as any).chineseEmojiKeywords = chineseEmojiKeywords;
  (window as any).searchChineseEmojis = searchChineseEmojis;
}

// 暂时隐藏转发功能
// const hasAttachmentMessages = (attachment: any): boolean => {
//   try {
//     return (
//       attachment &&
//       typeof attachment === 'object' &&
//       !Array.isArray(attachment) &&
//       Array.isArray(attachment.messages) &&
//       attachment.messages.length > 0
//     );
//   } catch {
//     return false;
//   }
// };

const getAttachmentUrl = (attachment: any): string | undefined => {
  try {
    if (!attachment) {
      return undefined;
    }
    if (typeof attachment === 'string') {
      return attachment;
    }
    if (typeof attachment === 'object' && !Array.isArray(attachment)) {
      return attachment.url || attachment.attachmentUrl || attachment.urlString;
    }
    return undefined;
  } catch {
    return undefined;
  }
};

// 暂时隐藏转发功能
// const isMergedForwardMessage = (msg: any): boolean => {
//   const msgAny = msg as any;
//   return (
//     msgAny.type === 'mergedForward' ||
//     msgAny.type === 5 ||
//     (msgAny.mergedForwardMessages &&
//       Array.isArray(msgAny.mergedForwardMessages) &&
//       msgAny.mergedForwardMessages.length > 0) ||
//     (msgAny.mergedForwardAttachmentUrl && typeof msgAny.mergedForwardAttachmentUrl === 'string') ||
//     hasAttachmentMessages(msgAny.attachment) ||
//     hasAttachmentMessages(msgAny.mergedForwardAttachment) ||
//     (typeof (msgAny.text || msgAny.message) === 'string' && (msgAny.text || msgAny.message).startsWith('!merged!'))
//   );
// };

// const extractForwardedMessages = (msg: any): any[] => {
//   const msgAny = msg as any;
//   try {
//     if (Array.isArray(msgAny.mergedForwardMessages) && msgAny.mergedForwardMessages.length > 0) {
//       return msgAny.mergedForwardMessages;
//     }
//     if (hasAttachmentMessages(msgAny.attachment)) {
//       return (msgAny.attachment as any).messages;
//     }
//     if (hasAttachmentMessages(msgAny.mergedForwardAttachment)) {
//       return (msgAny.mergedForwardAttachment as any).messages;
//     }
//     if (Array.isArray(msgAny.mergedForwardAttachment)) {
//       return msgAny.mergedForwardAttachment;
//     }
//   } catch (error) {
//     console.error('Error extracting forwarded messages:', error);
//   }
//   return [];
// };

export const WebChat = ({
  height,
  groupId,
  theme = 'light',
  disappearingMessages,
  launchOneOnOneWithFriend,
  discussionShareInGroup,
  onDiscussionTagsLoaded,
  onBatchSelectModeChange,
  onGroupInfoUpdated,
  onShareTargetSelectRequest,
  onFilterStateChange,
  batchHandlersRef
}: IWebChatProps) => {
  const [inputText, setInputText] = useState('');
  const [showUserSelection, setShowUserSelection] = useState(false);
  const [userSelectionQuery, setUserSelectionQuery] = useState('');
  const [groupInfo, setGroupInfo] = useState<any>({});
  const [mentionedUsers, setMentionedUsers] = useState<IUser[]>([]);
  const giftedChatRef = useRef<GiftedChat>(null);
  const deletingMessageIds = useRef<Set<number>>(new Set());
  const prevActiveFilterRef = useRef<FilterType>('none');
  const [showMessageSender, setShowMessageSender] = useState();
  const [showAcceptCard, setShowAcceptCard] = useState();
  const [pickFile, setPickFile] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{
    fileIndex: number;
    totalFiles: number;
    fileName: string;
    percent: number;
  } | null>(null);
  const isMountedRef = useRef(true);
  useEffect(() => chatSocket.retain(), []);

  const currentGroupIdRef = useRef(groupId);
  const isUploadingRef = useRef(false);
  const hasUserScrolledUnreadHistoryRef = useRef(false);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  useEffect(() => {
    currentGroupIdRef.current = groupId;
    hasUserScrolledUnreadHistoryRef.current = false;
    setUploadProgress(null);
    isUploadingRef.current = false;
  }, [groupId]);
  const { toast, render: renderToaster } = useToast();

  const {
    messages,
    setMessages,
    fromId,
    setFromId,
    scrollFromId,
    setScrollFromId,
    isLoadingEarlier,
    setIsLoadingEarlier,
    mapMessage,
    addMessage,
    loadMessages: loadMessagesBase,
    onNewMessage,
    onDeleteMessage,
    addServerHiddenMessage,
    scrollContainerRef,
    loadingMessagesRef: _loadingMessagesRef,
    unreadCount,
    firstUnreadMessageId,
    showUnreadIndicator,
    dismissUnreadIndicator
  } = useMessages({
    groupId,
    groupInfo,
    disappearingMessages,
    shareInGroup: discussionShareInGroup,
    onTagsLoaded: onDiscussionTagsLoaded
  });

  const {
    searchState,
    setSearchState,
    filterState,
    setFilterState,
    showSearchInput,
    setShowSearchInput,
    chineseSearchResults,
    setChineseSearchResults,
    showChineseResults,
    setShowChineseResults,
    searchText,
    applyFilter,
    getCurrentMessages,
    handleSearchChange,
    clearSearchState
  } = useMessageFilters({
    groupId,
    messages,
    mapMessage,
    onFilterStateChange,
    shareInGroup: discussionShareInGroup
  });

  // const [showShareMethodDialog, setShowShareMethodDialog] = useState(false); // 暂时隐藏转发功能
  const [confirmDialog, setConfirmDialog] = useState<{
    show: boolean;
    title: string;
    content: string;
    onConfirm: () => void;
    onCancel?: () => void;
  }>({
    show: false,
    title: '',
    content: '',
    onConfirm: () => {},
    onCancel: undefined
  });
  const [alertDialog, setAlertDialog] = useState<{
    show: boolean;
    content: string;
  }>({
    show: false,
    content: ''
  });

  // WebIDigestApp passes pixel heights ("800px"); vine-web passes calc()/dvh.
  // parseInt("calc(...)") is NaN and used to collapse the pane so the whole
  // page scrolled instead of fixing header + composer.
  const chatHeight = (() => {
    const trimmed = String(height || '').trim();
    const pxMatch = trimmed.match(/^(\d+(?:\.\d+)?)px$/i);
    if (pxMatch) {
      return `${Math.max(Number(pxMatch[1]) - 10, 0)}px`;
    }
    const asNumber = Number(trimmed);
    if (Number.isFinite(asNumber) && trimmed !== '') {
      return `${Math.max(asNumber - 10, 0)}px`;
    }
    // Fill the flex parent in IdigestChatShell (header is a sibling).
    return '100%';
  })();

  const uploadFilesToChat = useCallback(
    async (files: File[]): Promise<{ successCount: number; failedCount: number } | null> => {
      if (isUploadingRef.current) {
        return null;
      }
      isUploadingRef.current = true;
      const uploadingForGroupId = groupId;
      const isActive = () => isMountedRef.current && currentGroupIdRef.current === uploadingForGroupId;
      let successCount = 0;
      let failedCount = 0;
      try {
        let nextFilePromise = files[0] ? processImageForUpload(files[0]) : undefined;
        for (let i = 0; i < files.length; i++) {
          if (!isActive()) {
            break;
          }
          const originalFile = files[i];
          const uploadFile = await nextFilePromise!;
          if (!isActive()) {
            break;
          }

          // Start preparing the next image while the current file is uploading.
          nextFilePromise = files[i + 1] ? processImageForUpload(files[i + 1]) : undefined;
          setUploadProgress({ fileIndex: i + 1, totalFiles: files.length, fileName: originalFile.name, percent: 0 });
          try {
            const success = await _dataProvider.uploadFileWithChatMessage(uploadFile, groupId, undefined, {
              onUploadProgress: (progressEvent) => {
                if (!isActive()) {
                  return;
                }
                const percent = progressEvent.total
                  ? Math.round((progressEvent.loaded / progressEvent.total) * 100)
                  : 0;
                setUploadProgress((prev) =>
                  prev?.percent === percent
                    ? prev
                    : { fileIndex: i + 1, totalFiles: files.length, fileName: originalFile.name, percent }
                );
              }
            });
            if (!isActive()) {
              break;
            }
            if (success) {
              successCount++;
            } else {
              failedCount++;
            }
          } catch {
            failedCount++;
          }
        }
        if (isActive()) {
          setUploadProgress(null);
        }
      } finally {
        isUploadingRef.current = false;
      }
      return { successCount, failedCount };
    },
    [groupId]
  );

  const showUploadResult = useCallback(
    (successCount: number, failedCount: number, total: number) => {
      let alertMessage = '';
      if (successCount > 0) {
        alertMessage +=
          successCount < total
            ? i18n.t('GroupScreen.UploadPartialSuccess', { count: successCount }) + '\n'
            : i18n.t('GroupScreen.UploadSuccess') + '\n';
      }
      if (failedCount > 0) {
        alertMessage += i18n.t('GroupScreen.UploadFailed', { count: failedCount });
      }
      const intent = failedCount === 0 ? 'success' : successCount === 0 ? 'error' : 'info';
      toast({ intent, msg: alertMessage.trim() });
    },
    [toast]
  );

  const showAlertDialog = useCallback((message: string) => {
    setAlertDialog({
      show: true,
      content: message
    });
  }, []);

  const handlePaste = async (event: ClipboardEvent) => {
    if (!event.clipboardData) {
      return;
    }

    const items = event.clipboardData.items;
    const filesToUpload: File[] = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === 'file') {
        const blob = item.getAsFile();
        if (blob) {
          filesToUpload.push(blob as File);
        }
      }
    }

    if (filesToUpload.length > 0) {
      event.preventDefault();
      const result = await uploadFilesToChat(filesToUpload);
      if (result) {
        showUploadResult(result.successCount, result.failedCount, filesToUpload.length);
      }
    }
  };

  const { playingAudioId, playAudio, stopAudio } = useAudioPlayer();

  const {
    isRecording,
    recordingDuration,
    isVoiceMode,
    startRecording,
    stopRecording,
    toggleVoiceMode: toggleVoiceModeHook,
    formatDuration
  } = useVoiceRecording({
    groupId,
    messages,
    mapMessage,
    addMessage,
    onError: (msg) => toast({ intent: 'error', msg })
  });

  const toggleVoiceMode = useCallback(() => {
    toggleVoiceModeHook();
    if (isVoiceMode) {
      setInputText('');
    }
  }, [toggleVoiceModeHook, isVoiceMode]);

  const loadMessages = useCallback(
    (from: number, forSearch = false) => {
      if (forSearch) {
        let queryString = '';
        if (from !== -1) {
          queryString += `&from=${from}`;
        }
        queryString += `&search=${encodeURIComponent(searchText.current)}`;

        if (discussionShareInGroup !== undefined) {
          queryString += `&shareInGroups=${encodeURIComponent(JSON.stringify([discussionShareInGroup]))}`;
        }

        _dataProvider
          .getGroupMessage(groupId, queryString.substring(1))
          .then((groupMessages) => {
            if (!groupMessages || typeof groupMessages !== 'object') {
              console.error('Invalid getGroupMessage response:', groupMessages);
              setIsLoadingEarlier(false);
              return;
            }

            const messagesArray = Array.isArray(groupMessages) ? groupMessages : groupMessages.messages;
            if (!Array.isArray(messagesArray)) {
              setIsLoadingEarlier(false);
              return;
            }

            const { inSearch, searchedMessages } = searchState;
            if (from === -1) {
              setSearchState({ inSearch, searchedMessages: mapMessage(messagesArray) });
              setIsLoadingEarlier(false);
            } else {
              const newMessages = mapMessage(messagesArray);
              const messageIds = new Set(searchedMessages.map((m) => m?.id).filter((id) => id != null));
              const uniqueNewMessages = newMessages.filter((m: IMessage) => m && m.id && !messageIds.has(m.id));

              if (uniqueNewMessages.length > 0) {
                setSearchState({ inSearch, searchedMessages: [...uniqueNewMessages, ...searchedMessages] });
                setIsLoadingEarlier(false);
                setScrollFromId(-1);
              } else {
                setIsLoadingEarlier(false);
                setScrollFromId(-1);
              }
            }
          })
          .catch((error) => {
            console.error('Error loading search messages:', error);
            setIsLoadingEarlier(false);
          });
      } else {
        loadMessagesBase(from);
      }
    },
    [
      groupId,
      searchState,
      mapMessage,
      loadMessagesBase,
      setIsLoadingEarlier,
      setScrollFromId,
      setSearchState,
      searchText,
      discussionShareInGroup
    ]
  );

  const loadUser = useCallback(async () => {
    try {
      const latestGroupInfo = await _dataProvider.getGroupInfo(groupId);
      if (!latestGroupInfo || typeof latestGroupInfo !== 'object') {
        return;
      }
      if (currentGroupIdRef.current !== groupId) {
        return;
      }
      setGroupInfo(latestGroupInfo);
      onGroupInfoUpdated?.({ ...latestGroupInfo, groupId });
    } catch (_error) {}
  }, [groupId, onGroupInfoUpdated]);

  useEffect(() => {
    const fixEmojiData = () => {
      try {
        const keysToCheck = ['epr_suggested', 'epr_recent', 'epr_frequent'];
        keysToCheck.forEach((key) => {
          const data = localStorage.getItem(key);
          if (data) {
            try {
              const parsed = JSON.parse(data);
              // 只保留合法对象：不是null，是对象类型，不是数组
              if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
                localStorage.removeItem(key);
              }
            } catch (_parseError) {
              localStorage.removeItem(key);
            }
          }
        });

        Object.keys(localStorage).forEach((key) => {
          if (key.startsWith('epr_') || key.includes('emoji-picker')) {
            try {
              const data = localStorage.getItem(key);
              if (data) {
                const parsed = JSON.parse(data);
                // 只保留合法对象：不是null，是对象类型，不是数组
                if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
                  localStorage.removeItem(key);
                }
              }
            } catch (_e) {
              localStorage.removeItem(key);
            }
          }
        });
      } catch (_e) {
        Object.keys(localStorage).forEach((key) => {
          if (key.startsWith('epr_') || key.includes('emoji-picker')) {
            localStorage.removeItem(key);
          }
        });
      }
    };

    fixEmojiData();
  }, []);

  useEffect(() => {
    setFromId(-1);
    setScrollFromId(-1);
    setIsLoadingEarlier(false);
    setMessages([]);
    setInputText('');
    setMentionedUsers([]);
    loadMessages(-1);
    void loadUser();
    setFilterState({
      activeFilter: 'none',
      filteredMessages: messages
    });
    prevActiveFilterRef.current = 'none';
    setSearchState({ inSearch: false, searchedMessages: [] });
    setShowSearchInput(false);
    setIsBatchSelectMode(false);
    setSelectedMessageIds(new Set());
    searchText.current = '';

    setTimeout(() => {
      if (scrollContainerRef.current) {
        const container = scrollContainerRef.current;
        requestAnimationFrame(() => {
          container.scrollTop = container.scrollHeight - container.clientHeight;
        });
      }
    }, 100);

    return () => {
      stopAudio(true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId, discussionShareInGroup]);

  useEffect(() => {
    if (fromId !== -1) {
      loadMessages(fromId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromId]);

  useEffect(() => {
    if (scrollFromId !== -1) {
      loadMessages(scrollFromId, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollFromId]);

  useEffect(() => {
    return chatSocket.subscribe(groupId, {
      newMessageCallback: onNewMessage,
      deleteMessageCallback: onDeleteMessage,
      newGroupInfoCallback: () => {
        void loadUser();
      }
    });
  }, [groupId, onNewMessage, onDeleteMessage, loadUser]);

  useEffect(() => {
    const setupEmojiPickerData = () => {
      try {
        const defaultEmojis = ['👆', '👌', '👍', '😭', '❤️', '👎', '👏', '👇', '🎆'];
        const defaultEmojiArray = defaultEmojis.map((emoji) => emoji.codePointAt(0)?.toString(16)).filter(Boolean);

        let currentSuggested = [];
        try {
          const existingSuggested = localStorage.getItem('epr_suggested');
          if (existingSuggested) {
            const parsed = JSON.parse(existingSuggested);
            if (Array.isArray(parsed)) {
              currentSuggested = parsed;
            }
          }
        } catch {
          // Ignore parse errors
        }

        if (currentSuggested.length === 0) {
          localStorage.setItem('epr_suggested', JSON.stringify(defaultEmojiArray));
          localStorage.setItem('epr_recent', JSON.stringify([]));
          localStorage.setItem('epr_frequent', JSON.stringify(defaultEmojiArray));
        } else {
          const mergedSuggested = [...defaultEmojiArray];

          currentSuggested.forEach((emojiCode: string) => {
            if (!mergedSuggested.includes(emojiCode)) {
              mergedSuggested.push(emojiCode);
            }
          });

          const finalSuggested = mergedSuggested.slice(0, 20);
          localStorage.setItem('epr_suggested', JSON.stringify(finalSuggested));
        }
      } catch (e) {
        console.error('Failed to setup emoji picker data:', e);
      }
    };

    setupEmojiPickerData();
  }, [groupId]);

  const sendMessage = useCallback(
    (msgtext: string, replyMsgId = -1) => {
      const text = msgtext.trim();
      if (text.length === 0) {
        return;
      }

      const mentioned = [];
      if (text.indexOf(`@${theAllUserUser.name}`) !== -1) {
        groupInfo.users?.forEach((user: any) => mentioned.push(user.userId));
      } else {
        for (const user of mentionedUsers) {
          if (text.indexOf(`@${user.name}`) !== -1) {
            mentioned.push(user.userId);
          }
        }
      }
      const latestMessageId = messages.length > 0 ? messages[0].id : 0;
      _dataProvider
        .postGroupMessage(groupId, text, replyMsgId, mentioned)
        .then((newMessageId) => {
          // 使用明确的类型检查，避免 truthy/falsy 陷阱
          if (typeof newMessageId === 'number' && newMessageId > 0) {
            // 成功，有明确的 messageId
            _dataProvider
              .getGroupMessage(groupId, `to=${latestMessageId + 1}&from=${latestMessageId + 10000}`)
              .then((groupMessages) => {
                if (!groupMessages || typeof groupMessages !== 'object') {
                  console.error('Invalid getGroupMessage response after post:', groupMessages);
                  return;
                }
                const messagesArray = Array.isArray(groupMessages) ? groupMessages : groupMessages.messages;
                if (Array.isArray(messagesArray) && messagesArray.length > 0) {
                  try {
                    addMessage(mapMessage(messagesArray));
                  } catch (error) {
                    console.error('Error processing messages after post:', error);
                  }
                }
              })
              .catch((error) => {
                console.error('Error loading messages after post:', error);
              });
          } else if (newMessageId === 'SUCCESS_NO_ID') {
            // 成功，无 messageId，消息会通过 WebSocket 推送
            // 等待一小段时间，让 WebSocket 消息有机会到达
            // 如果 WebSocket 消息已经到达，addMessage 会处理重复消息
            // 注意：不显示错误提示，因为消息可能已经通过 WebSocket 推送了
            // 如果消息没有出现，用户会自然知道发送失败了
            setTimeout(() => {
              // 如果还没有收到，尝试通过 API 获取（可能是 WebSocket 延迟或未连接）
              // 但不显示错误，因为消息可能已经通过 WebSocket 推送了
              _dataProvider
                .getGroupMessage(groupId, `to=${latestMessageId + 1}&from=${latestMessageId + 10000}`)
                .then((groupMessages) => {
                  if (!groupMessages || typeof groupMessages !== 'object') {
                    // 不显示错误，因为消息可能已经通过 WebSocket 推送了
                    return;
                  }
                  const messagesArray = Array.isArray(groupMessages) ? groupMessages : groupMessages.messages;
                  if (Array.isArray(messagesArray) && messagesArray.length > 0) {
                    try {
                      addMessage(mapMessage(messagesArray));
                      // 成功获取到新消息，说明发送成功
                    } catch (error) {
                      console.error('Error processing messages after post:', error);
                      // 不显示错误，因为消息可能已经通过 WebSocket 推送了
                    }
                  }
                  // 如果没有获取到新消息，也不显示错误
                  // 因为消息可能已经通过 WebSocket 推送了，只是 API 还没有同步
                })
                .catch((error) => {
                  console.error('Error loading messages after post:', error);
                  // 不显示错误，因为消息可能已经通过 WebSocket 推送了
                });
            }, 500); // 等待 500ms，让 WebSocket 消息有机会到达
          } else {
            // newMessageId === 'FAILED'，请求失败
            toast({ intent: 'error', msg: 'send failed, network might be down...' });
          }
        })
        .catch((error) => {
          console.error('Error posting message:', error);
          toast({ intent: 'error', msg: 'send failed, network might be down...' });
        });
    },
    [groupId, groupInfo, mentionedUsers, messages, addMessage, mapMessage, toast]
  );

  const onSend = (newmessages: any) => {
    if (!groupInfo || typeof groupInfo !== 'object') {
      console.error('groupInfo not available in sendMessage');
      return;
    }

    const onlyLeaderCanPost =
      groupInfo.onlyLeaderCanPost === true || groupInfo.onlyLeaderCanPost === 1 || groupInfo.onlyLeaderCanPost === '1';
    if (onlyLeaderCanPost) {
      const jwtObj = getJwtObj();
      if (!jwtObj || !jwtObj.userId) {
        console.error('Invalid JWT object in sendMessage');
        return;
      }

      const uuu = groupInfo.users?.find((uu: any) => jwtObj.userId === uu?.userId);
      if (!uuu || !(uuu.isGroupLeader === true || uuu.isGroupLeader === 1 || uuu.isGroupLeader === '1')) {
        toast({ intent: 'error', msg: i18n.t('Chatroom.OnlyLeaderCanPost') });
        return;
      }
    }
    sendMessage(newmessages[0].text);
    setInputText('');
  };

  const currentUserCanPost = useMemo(() => {
    const onlyLeaderCanPost =
      groupInfo?.onlyLeaderCanPost === true ||
      groupInfo?.onlyLeaderCanPost === 1 ||
      groupInfo?.onlyLeaderCanPost === '1';
    if (!onlyLeaderCanPost) {
      return true;
    }

    const currentUserId = getJwtObj()?.userId;
    if (currentUserId === undefined || currentUserId === null || String(currentUserId) === '-1') {
      return false;
    }

    const currentUser = Array.isArray(groupInfo.users)
      ? groupInfo.users.find((user: any) => {
          const userId = user?.userId ?? user?.id;
          return userId != null && String(currentUserId) === String(userId);
        })
      : undefined;

    return (
      currentUser?.isGroupLeader === true || currentUser?.isGroupLeader === 1 || currentUser?.isGroupLeader === '1'
    );
  }, [groupInfo]);

  const currentUserCannotPost = !currentUserCanPost;

  useEffect(() => {
    if (currentUserCannotPost) {
      setInputText('');
      setShowUserSelection(false);
      setShowEmojiPicker(false);
    }
  }, [currentUserCannotPost]);

  const isNearTop = (layoutHeight: number, contentOffset: number, contentHeight: number) => {
    const threshold = 100;

    const contentH = Math.round(contentHeight);
    const layoutH = Math.round(layoutHeight);

    if (contentH <= layoutH) {
      return false;
    }

    const result = Math.round(contentOffset) <= threshold;
    return result;
  };

  const loadEarlierMessagesAsync = (forSearch = false) => {
    if (isLoadingEarlier) {
      return;
    }

    if (forSearch) {
      const { searchedMessages } = searchState;
      if (searchedMessages.length === 0) {
        return;
      }
      const earliestMessageId = searchedMessages[0].id;
      if (scrollFromId === earliestMessageId) {
        return;
      }
      setIsLoadingEarlier(true);
      setScrollFromId(earliestMessageId);
    } else {
      if (messages.length === 0) {
        return;
      }
      const earliestMessageId = messages[0].id;
      if (fromId === earliestMessageId) {
        return;
      }
      setIsLoadingEarlier(true);
      setFromId(earliestMessageId);
    }
  };

  const onInputTextChanged = (text: string) => {
    if (currentUserCannotPost) {
      return;
    }
    if (searchState.inSearch) {
      return;
    }
    setInputText(text);
    if (text[text.length - 1] === '@') {
      setUserSelectionQuery('');
      setShowUserSelection(true);
    }
  };

  const handleScroll = (e: any) => {
    if (e.target && !scrollContainerRef.current) {
      scrollContainerRef.current = e.target;
    }

    if (isNearTop(e.target.clientHeight, e.target.scrollTop, e.target.scrollHeight)) {
      loadEarlierMessagesAsync(searchState.inSearch);
    }

    if (hasUserScrolledUnreadHistoryRef.current && firstUnreadMessageId !== null) {
      const firstUnread = e.target.querySelector(`[data-message-id="${firstUnreadMessageId}"]`);
      if (firstUnread) {
        const containerRect = e.target.getBoundingClientRect();
        const messageRect = firstUnread.getBoundingClientRect();
        if (messageRect.top >= containerRect.top && messageRect.bottom <= containerRect.bottom) {
          dismissUnreadIndicator();
        }
      }
    }
  };

  const markUnreadHistoryScrolled = () => {
    hasUserScrolledUnreadHistoryRef.current = true;
  };

  const scrollToFirstUnreadMessage = () => {
    if (getIdigestChatHostConfig().renderMessageList && firstUnreadMessageId !== null) {
      if (
        getCurrentMessages().some((message) => message.id === firstUnreadMessageId) &&
        giftedChatRef.current?.scrollToMessage(firstUnreadMessageId)
      ) {
        dismissUnreadIndicator();
      }
      return;
    }
    const container = scrollContainerRef.current;
    if (!container || firstUnreadMessageId === null) {
      return;
    }
    const message = container.querySelector<HTMLElement>(`[data-message-id="${firstUnreadMessageId}"]`);
    if (message) {
      const containerRect = container.getBoundingClientRect();
      const messageRect = message.getBoundingClientRect();
      const targetTop = container.scrollTop + messageRect.top - containerRect.top;
      container.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' });
      dismissUnreadIndicator();
    }
  };

  const renderUserInSelection = (user: IUser) => {
    user.tag = _dataProvider.getMyTagForUser(user.id || user.userId);
    return (
      <div
        key={user.userId}
        className='avatar-wrapper'
        onClick={() => {
          setShowUserSelection(false);
          if (user.userId !== -2 && mentionedUsers.indexOf(user) === -1) {
            setMentionedUsers([user, ...mentionedUsers]);
          }
          setInputText(inputText + user.name + ' ');
          giftedChatRef?.current?.focusTextInput();
        }}
      >
        {userAvatarName(user, 3, false, '40px', 10)}
      </div>
    );
  };

  const filterFilterStateMessage = useCallback(
    (messageId: number) => {
      setFilterState((prevState) => ({
        ...prevState,
        filteredMessages: prevState.filteredMessages.filter((msg) => msg.id !== messageId)
      }));
    },
    [setFilterState]
  );

  const isUserGroupLeader = useCallback(
    (userId: any): boolean => {
      if (!userId || !groupInfo || !groupInfo.users || !Array.isArray(groupInfo.users)) {
        return false;
      }
      const target = groupInfo.users.find((uu: any) => {
        const uuId = uu.userId ?? uu.id;
        return uuId != null && String(userId) === String(uuId);
      });
      return !!target && (target.isGroupLeader === true || target.isGroupLeader === 1);
    },
    [groupInfo]
  );

  const checkCurrentUserIsLeader = useCallback((): boolean => {
    const currentUserId = getJwtObj()?.userId;
    if (currentUserId === undefined || currentUserId === null || String(currentUserId) === '-1') {
      return false;
    }
    return isUserGroupLeader(currentUserId);
  }, [isUserGroupLeader]);

  const canOpenUserProfile = useCallback(
    (userId: any): boolean => {
      if (userId === undefined || userId === null) {
        return false;
      }
      if (!groupInfo?.disableMemberShowList) {
        return true;
      }
      const currentUserId = getJwtObj()?.userId;
      if (currentUserId === undefined || currentUserId === null || String(currentUserId) === '-1') {
        return false;
      }
      if (String(userId) === String(currentUserId)) {
        return true;
      }
      if (checkCurrentUserIsLeader()) {
        return true;
      }
      return isUserGroupLeader(userId);
    },
    [checkCurrentUserIsLeader, groupInfo?.disableMemberShowList, isUserGroupLeader]
  );

  const getMessageTextForCollection = useCallback(
    async (msg: any, currentGroupId?: number, _skipSolidification = false): Promise<{ text: string }> => {
      // 暂时隐藏转发功能 - 合并转发消息处理
      // const isMergedForward = msg.type === 'mergedForward' || msg.type === 5;

      // if (isMergedForward) {
      //   let text = msg.message || msg.text || '';
      //   if (!text.startsWith('!merged!') && msg.mergedForwardTitle && msg.mergedForwardContent) {
      //     try {
      //       text = `!merged!${encodeURIComponent(msg.mergedForwardTitle)}!${encodeURIComponent(
      //         msg.mergedForwardContent
      //       )}`;
      //     } catch (e) {
      //       text = `${msg.mergedForwardTitle}: ${msg.mergedForwardContent}`;
      //     }
      //   }

      //   if (
      //     msg.mergedForwardMessages &&
      //     Array.isArray(msg.mergedForwardMessages) &&
      //     msg.mergedForwardMessages.length > 0
      //   ) {
      //     try {
      //       text = `${text}|||MERGED_MESSAGES:${encodeURIComponent(JSON.stringify(msg.mergedForwardMessages))}`;
      //     } catch (e) {
      //       console.error('Failed to serialize mergedForwardMessages:', e);
      //     }
      //   } else if (msg.mergedForwardAttachmentUrl) {
      //     if (!skipSolidification && currentGroupId) {
      //       try {
      //         let attachmentUrl = msg.mergedForwardAttachmentUrl;
      //         const groupIdMatch = attachmentUrl.match(/\/chat\/(\d+)\//);
      //         if (groupIdMatch?.[1]) {
      //           const urlGroupId = parseInt(groupIdMatch[1], 10);
      //           if (urlGroupId !== currentGroupId) {
      //             attachmentUrl = attachmentUrl.replace(/\/chat\/\d+\//, `/chat/${currentGroupId}/`);
      //           }
      //         }

      //         const response = await fetch(attachmentUrl);
      //         if (response.ok) {
      //           const data = await response.json();
      //           const messagesArray = Array.isArray(data) ? data : data?.messages;
      //           if (messagesArray?.length > 0) {
      //             text = `${text}|||MERGED_MESSAGES:${encodeURIComponent(JSON.stringify(messagesArray))}`;
      //             return { text };
      //           }
      //         }
      //       } catch (error) {
      //         // Fall through to save URL
      //       }
      //     }
      //     text = `${text}|||ATTACHMENT_URL:${msg.mergedForwardAttachmentUrl}`;
      //   }

      //   return { text };
      // }
      return { text: msg.message || msg.text || '' };
    },
    []
  );

  const removeCollectionMessage = useCallback(
    async (type: 'user' | 'group', msg: any, silent = false): Promise<boolean> => {
      if (!msg || !msg.id) {
        if (!silent) {
          showAlertDialog(i18n.t('HandleFailed'));
        }
        return false;
      }

      if (type === 'group' && !checkCurrentUserIsLeader()) {
        if (!silent) {
          toast({ intent: 'error', msg: i18n.t('Chatroom.OnlyLeaderCanPost') });
        }
        return false;
      }

      try {
        const success = await _dataProvider.removeCollectionSource(groupId, type, msg.id);
        if (success) {
          filterFilterStateMessage(msg.id);
        } else {
          if (!silent) {
            showAlertDialog(i18n.t('Chatroom.BatchRemoveCollectionFailed', { count: 1 }));
          }
        }
        return success;
      } catch (error) {
        console.error('Error removing collection message:', error);
        if (!silent) {
          showAlertDialog(i18n.t('HandleFailed'));
        }
        return false;
      }
    },
    [checkCurrentUserIsLeader, groupId, filterFilterStateMessage, showAlertDialog, toast]
  );

  const {
    isBatchSelectMode,
    setIsBatchSelectMode,
    selectedMessageIds,
    setSelectedMessageIds,
    toggleMessageSelection,
    exitBatchSelectMode,
    selectMsg,
    shareMsg,
    handleBatchGroupCollection,
    handleBatchRemoveGroupCollection,
    handleBatchMyCollection,
    handleBatchRemoveMyCollection,
    handleBatchDelete
    // handleBatchShare, // 暂时隐藏转发功能
    // hasMergedForwardMessage, // 暂时隐藏转发功能
    // handleShareMethod // 暂时隐藏转发功能
  } = useBatchSelection({
    groupId,
    messages,
    getCurrentMessages,
    checkCurrentUserIsLeader,
    getMessageTextForCollection,
    removeCollectionMessage,
    showAlertDialog,
    setMessages,
    onBatchSelectModeChange,
    onShareTargetSelectRequest,
    setConfirmDialog
    // setShowShareMethodDialog // 暂时隐藏转发功能
  });

  const deleteMsgInternal = useCallback(
    async (msg: any) => {
      if (!msg || !msg.id) {
        return;
      }

      if (deletingMessageIds.current.has(msg.id)) {
        return;
      }

      deletingMessageIds.current.add(msg.id);

      try {
        let success = false;
        if (filterState.activeFilter === 'none') {
          success = await _dataProvider.deleteChatMessage(groupId, msg.id);
          if (success) {
            setMessages((prevMessages) => prevMessages.filter((m) => m.id !== msg.id));
          } else {
            showAlertDialog(i18n.t('Chatroom.BatchDeleteFailed', { count: 1 }));
          }
        } else {
          success = await _dataProvider.removeCollectionSource(groupId, filterState.activeFilter, msg.id);
          if (success) {
            filterFilterStateMessage(msg.id);
          } else {
            showAlertDialog(i18n.t('Chatroom.BatchRemoveCollectionFailed', { count: 1 }));
          }
        }
      } catch (error) {
        console.error('Error deleting message:', error);
        showAlertDialog(i18n.t('Chatroom.BatchDeleteFailed', { count: 1 }));
      } finally {
        deletingMessageIds.current.delete(msg.id);
      }
    },
    [filterState.activeFilter, groupId, showAlertDialog, filterFilterStateMessage, setMessages]
  );

  const deleteMsg = useCallback(
    async (msg: any, skipConfirm = false) => {
      if (!msg || !msg.id) {
        return;
      }

      if (deletingMessageIds.current.has(msg.id)) {
        return;
      }

      if (!skipConfirm) {
        setConfirmDialog({
          show: true,
          title: '请确认',
          content: i18n.t('Chatroom.ConfirmDelete', { count: 1 }),
          onConfirm: () => {
            setConfirmDialog((prev) => ({ ...prev, show: false }));
            deleteMsgInternal(msg);
          },
          onCancel: () => {
            setConfirmDialog((prev) => ({ ...prev, show: false }));
          }
        });
        return;
      }

      deleteMsgInternal(msg);
    },
    [deleteMsgInternal, setConfirmDialog]
  );

  const hideMsgInternal = useCallback(
    async (msg: any) => {
      if (!msg || !msg.id) {
        return;
      }

      const currentUserId = getJwtObj()?.userId;
      if (currentUserId === undefined || currentUserId === null) {
        return;
      }
      if (msg.user?.id != null && String(msg.user.id) === String(currentUserId)) {
        return;
      }

      const success = await _dataProvider.hideChatMessage(groupId, msg.id);
      if (success) {
        addServerHiddenMessage(msg.id);
      } else {
        // Hiding is server-authoritative; if the server did not record the hide, surface the
        // failure instead of faking a client-only hide that would diverge from server state.
        toast({ intent: 'error', msg: i18n.t('Chatroom.HideFailed') });
      }
    },
    [addServerHiddenMessage, groupId, toast]
  );

  const hideMsg = useCallback(
    (msg: any) => {
      if (!msg || !msg.id) {
        return;
      }

      setConfirmDialog({
        show: true,
        title: i18n.t('Chatroom.HideConfirmTitle'),
        content: i18n.t('Chatroom.HideConfirmMessage'),
        onConfirm: () => {
          setConfirmDialog((prev) => ({ ...prev, show: false }));
          hideMsgInternal(msg);
        },
        onCancel: () => {
          setConfirmDialog((prev) => ({ ...prev, show: false }));
        }
      });
    },
    [hideMsgInternal, setConfirmDialog]
  );

  const replyMsg = useCallback(
    (msg: any) => {
      const replyText = prompt(i18n.t('Chatroom.ReplyPrompt'));
      if (replyText) {
        sendMessage(replyText, msg.id);
      }
    },
    [sendMessage]
  );

  const updateCollectionMessage = useCallback(
    async (type: 'user' | 'group', msg: any, silent = false): Promise<boolean> => {
      if (!msg || !msg.user) {
        if (!silent) {
          showAlertDialog(i18n.t('HandleFailed'));
        }
        return false;
      }

      if (type === 'group' && !checkCurrentUserIsLeader()) {
        if (!silent) {
          toast({ intent: 'error', msg: i18n.t('Chatroom.OnlyLeaderCanPost') });
        }
        return false;
      }

      try {
        const { text: messageText } = await getMessageTextForCollection(msg, groupId, true);
        const success = await _dataProvider.updateCollectionMessage(groupId, type, {
          message: messageText,
          user: msg.user.id,
          time: msg.time,
          messageId: msg.id
        });
        if (!silent) {
          if (success) {
            showAlertDialog(i18n.t('Chatroom.BatchCollectionSuccess', { count: 1 }));
          } else {
            showAlertDialog(i18n.t('Chatroom.BatchCollectionFailed', { count: 1 }));
          }
        }
        return success;
      } catch (error) {
        console.error('Error updating collection message:', error);
        if (!silent) {
          showAlertDialog(i18n.t('HandleFailed'));
        }
        return false;
      }
    },
    [checkCurrentUserIsLeader, groupId, showAlertDialog, getMessageTextForCollection, toast]
  );

  useEffect(() => {
    if (onFilterStateChange) {
      onFilterStateChange(filterState.activeFilter);
    }
  }, [filterState.activeFilter, onFilterStateChange]);

  useEffect(() => {
    if (
      isBatchSelectMode &&
      prevActiveFilterRef.current === 'none' &&
      (filterState.activeFilter === 'user' ||
        filterState.activeFilter === 'group' ||
        filterState.activeFilter === 'file')
    ) {
      exitBatchSelectMode();
    }
    prevActiveFilterRef.current = filterState.activeFilter;
  }, [filterState.activeFilter, isBatchSelectMode, exitBatchSelectMode]);

  useEffect(() => {
    if (batchHandlersRef) {
      batchHandlersRef.current = {
        onExit: exitBatchSelectMode,
        onGroupCollection: handleBatchGroupCollection,
        onMyCollection: handleBatchMyCollection,
        onDelete: handleBatchDelete,
        // onShare: handleBatchShare, // 暂时隐藏转发功能
        onRemoveGroupCollection: handleBatchRemoveGroupCollection,
        onRemoveMyCollection: handleBatchRemoveMyCollection
      };
    }
  }, [
    batchHandlersRef,
    exitBatchSelectMode,
    handleBatchGroupCollection,
    handleBatchMyCollection,
    handleBatchDelete,
    // handleBatchShare, // 暂时隐藏转发功能
    handleBatchRemoveGroupCollection,
    handleBatchRemoveMyCollection
  ]);

  const messageActionSheet = useCallback(
    (msg: any) => {
      const actions: any[] = [];
      if (!msg) {
        return actions;
      }

      const currentUserId = getJwtObj()?.userId;
      const currentUserIsLeader = checkCurrentUserIsLeader();
      const isCurrentUserMessage =
        currentUserId != null &&
        String(currentUserId) !== '-1' &&
        msg.user?.id != null &&
        String(msg.user.id) === String(currentUserId);

      let allowDelete = isCurrentUserMessage;
      if (!allowDelete) {
        allowDelete = currentUserIsLeader;
      }
      if (filterState.activeFilter === 'none' || !filterState.activeFilter || filterState.activeFilter === 'file') {
        actions.push({
          type: 'myCollection',
          label: i18n.t('Chatroom.MyCollectionShort'),
          icon: myCollectionIcon,
          action: (m: any) => updateCollectionMessage('user', m),
          disabled: false
        });
      } else if (filterState.activeFilter === 'user') {
        actions.push({
          type: 'removeMyCollection',
          label: i18n.t('Chatroom.MyCollectionShort'),
          icon: removeMyCollectionIcon,
          action: (m: any) => removeCollectionMessage('user', m),
          disabled: false
        });
        actions.push({
          type: 'groupCollection',
          label: i18n.t('Chatroom.GroupCollectionShort'),
          icon: groupCollectionIcon,
          action: (m: any) => updateCollectionMessage('group', m),
          disabled: !currentUserIsLeader
        });
      } else if (filterState.activeFilter === 'group') {
        actions.push({
          type: 'removeGroupCollection',
          label: i18n.t('Chatroom.GroupCollectionShort'),
          icon: removeGroupCollectionIcon,
          action: (m: any) => removeCollectionMessage('group', m),
          disabled: !currentUserIsLeader
        });
        actions.push({
          type: 'myCollection',
          label: i18n.t('Chatroom.MyCollectionShort'),
          icon: myCollectionIcon,
          action: (m: any) => updateCollectionMessage('user', m),
          disabled: false
        });
      }

      if (filterState.activeFilter === 'none' || !filterState.activeFilter || filterState.activeFilter === 'file') {
        actions.push({
          type: 'groupCollection',
          label: i18n.t('Chatroom.GroupCollectionShort'),
          icon: groupCollectionIcon,
          action: (m: any) => updateCollectionMessage('group', m),
          disabled: !currentUserIsLeader
        });
      }

      actions.push({
        type: 'reply',
        label: i18n.t('Chatroom.Reply'),
        icon: replyIcon,
        action: replyMsg,
        disabled: false
      });

      actions.push({
        type: 'share',
        label: i18n.t('Share'),
        icon: forwardIcon,
        action: shareMsg,
        disabled: false
      });

      if (!isCurrentUserMessage) {
        actions.push({
          type: 'hide',
          label: i18n.t('Chatroom.Hide'),
          icon: hideIcon,
          action: hideMsg,
          disabled: false
        });
      }

      actions.push({
        type: 'delete',
        label: i18n.t('authoring.Delete'),
        icon: deleteIcon,
        action: deleteMsg,
        disabled: !allowDelete
      });

      actions.push({
        type: 'select',
        label: i18n.t('Chatroom.Select'),
        icon: selectIcon,
        action: selectMsg,
        disabled: false
      });

      return actions;
    },
    [
      checkCurrentUserIsLeader,
      filterState.activeFilter,
      updateCollectionMessage,
      removeCollectionMessage,
      replyMsg,
      shareMsg,
      hideMsg,
      deleteMsg,
      selectMsg
    ]
  );

  const handleQuotePress = useCallback((msg: any) => {
    const quoteText = msg.quote || '';
    if (quoteText) {
      alert(quoteText);
    }
  }, []);

  const validateAndOpenUrl = useCallback(
    (url: string, errorMessageKey = 'Chatroom.urlInvalid'): boolean => {
      return validateAndOpenUrlUtil(url, () => {
        toast({ intent: 'error', msg: i18n.t(errorMessageKey) });
      });
    },
    [toast]
  );

  const _extractFileUrlFromMessage = useCallback((messageText: string, groupId: number): string | undefined => {
    try {
      if (!messageText || !groupId) {
        return undefined;
      }

      const parts = messageText.split('!');
      let jsonFilename: string | null = null;

      if (parts.length > 4 && parts[4]) {
        const part4 = parts[4].trim();
        if (part4.includes('.json')) {
          const filenameMatch = part4.match(/([^?#\s]+\.json)/);
          if (filenameMatch) {
            jsonFilename = filenameMatch[1];
          }
        }
      }

      if (!jsonFilename && parts.length > 0) {
        const lastPart = parts[parts.length - 1].trim();
        if (lastPart.includes('.json')) {
          const filenameMatch = lastPart.match(/([^?#\s]+\.json)/);
          if (filenameMatch) {
            jsonFilename = filenameMatch[1];
          }
        }
      }

      if (!jsonFilename) {
        const globalMatch = messageText.match(/([^!?#\s/\\]+\.json)/);
        if (globalMatch) {
          jsonFilename = globalMatch[1];
        }
      }

      if (!jsonFilename) {
        return undefined;
      }

      const numericMatch = jsonFilename.match(/(\d+)\.json$/);
      if (numericMatch && numericMatch[1]) {
        const parsedId = parseInt(numericMatch[1], 10);
        if (!isNaN(parsedId) && parsedId > 0) {
          return _dataProvider.getFileUrlInChat(groupId, parsedId);
        }
      }

      return undefined;
    } catch (error) {
      console.error('Error extracting file URL from message:', error);
      return undefined;
    }
  }, []);

  // 暂时隐藏转发功能
  // const attachmentCacheRef = useRef<Map<string, any[]>>(new Map());

  const onPress = useCallback(
    (msg: any) => {
      try {
        const msgAny = msg as any;

        // 暂时隐藏转发功能 - 合并转发消息处理
        // if (isMergedForwardMessage(msgAny)) {
        //   if (onOpenMergedForward) {
        //     if (msgAny.mergedForwardMessages?.length > 0) {
        //       onOpenMergedForward(msgAny.mergedForwardMessages);
        //       return;
        //     }

        //     const forwardedMessages = extractForwardedMessages(msgAny);
        //     if (forwardedMessages.length > 0) {
        //       onOpenMergedForward(forwardedMessages);
        //       return;
        //     }

        //     const messageText = msgAny.message || msgAny.text || '';
        //     if (messageText?.startsWith('!merged!')) {
        //       const mergedMessagesMatch = messageText.match(/\|\|\|MERGED_MESSAGES:(.+)$/);
        //       if (mergedMessagesMatch) {
        //         try {
        //           const parsedMessages = JSON.parse(decodeURIComponent(mergedMessagesMatch[1]));
        //           if (parsedMessages.length > 0) {
        //             onOpenMergedForward(parsedMessages);
        //             return;
        //           }
        //         } catch (e) {
        //           // Ignore parse error
        //         }
        //       }
        //     }

        //     if (hasAttachmentMessages(msgAny.attachment)) {
        //       const attachmentMessages = (msgAny.attachment as any).messages;
        //       if (attachmentMessages?.length > 0) {
        //         onOpenMergedForward(attachmentMessages);
        //         return;
        //       }
        //     }

        //     if (hasAttachmentMessages(msgAny.mergedForwardAttachment)) {
        //       const attachmentMessages = (msgAny.mergedForwardAttachment as any).messages;
        //       if (attachmentMessages?.length > 0) {
        //         onOpenMergedForward(attachmentMessages);
        //         return;
        //       }
        //     }

        //     if (msgAny.attachment && typeof msgAny.attachment === 'string') {
        //       try {
        //         const attachmentData = JSON.parse(msgAny.attachment);
        //         const messagesArray = Array.isArray(attachmentData) ? attachmentData : attachmentData?.messages;
        //         if (messagesArray?.length > 0) {
        //           onOpenMergedForward(messagesArray);
        //           return;
        //         }
        //       } catch (error) {
        //         // Not JSON, continue
        //       }
        //     }

        //     let attachmentUrl: string | undefined =
        //       msgAny.mergedForwardAttachmentUrl ||
        //       getAttachmentUrl(msgAny.attachment) ||
        //       getAttachmentUrl(msgAny.mergedForwardAttachment);

        //     if (!attachmentUrl && messageText) {
        //       if (messageText.startsWith('!merged!')) {
        //         const attachmentUrlMatch = messageText.match(/\|\|\|ATTACHMENT_URL:(.+)$/);
        //         if (attachmentUrlMatch) {
        //           attachmentUrl = attachmentUrlMatch[1];
        //         } else {
        //           const parts = messageText.split('!');
        //           if (parts.length >= 5) {
        //             const urlPart = parts.slice(4).join('!');
        //             if (urlPart?.startsWith('http')) {
        //               attachmentUrl = urlPart;
        //             } else if (urlPart) {
        //               const fileIdMatch = urlPart.match(/(\d+)(?:\.json)?/);
        //               if (fileIdMatch?.[1]) {
        //                 attachmentUrl = _dataProvider.getFileUrlInChat(groupId, parseInt(fileIdMatch[1], 10));
        //               }
        //             }
        //           }
        //         }
        //       }

        //       if (!attachmentUrl && messageText.includes('.json')) {
        //         attachmentUrl = _extractFileUrlFromMessage(messageText, groupId);
        //       }
        //     }

        //     if (!attachmentUrl) {
        //       const messageId = msgAny.messageId || msgAny.id;
        //       if (messageId) {
        //         const numericId = typeof messageId === 'string' ? parseInt(messageId, 10) : messageId;
        //         if (!isNaN(numericId) && numericId > 0) {
        //           attachmentUrl = _dataProvider.getFileUrlInChat(groupId, numericId);
        //         }
        //       }
        //     }

        //     if (attachmentUrl) {
        //       const cacheKey = attachmentUrl;
        //       const cachedMessages = attachmentCacheRef.current.get(cacheKey);
        //       if (cachedMessages && cachedMessages.length > 0) {
        //         onOpenMergedForward(cachedMessages);
        //         return;
        //       }

        //       let finalAttachmentUrl = attachmentUrl;
        //       const groupIdMatch = attachmentUrl.match(/\/chat\/(\d+)\//);
        //       if (groupIdMatch?.[1]) {
        //         const urlGroupId = parseInt(groupIdMatch[1], 10);
        //         if (urlGroupId !== groupId) {
        //           finalAttachmentUrl = attachmentUrl.replace(/\/chat\/\d+\//, `/chat/${groupId}/`);
        //         }
        //       }

        //       const isOldFormat = messageText?.startsWith('!merged!') && !messageText.includes('|||MERGED_MESSAGES:');

        //       fetch(finalAttachmentUrl)
        //         .then((res) => {
        //           if (!res.ok) {
        //             if (res.status === 404 && finalAttachmentUrl !== attachmentUrl) {
        //               return fetch(attachmentUrl).then((originalRes) => {
        //                 if (!originalRes.ok) {
        //                   if (originalRes.status === 404) {
        //                     throw new Error(
        //                       'Attachment file not found. The attachment might not have been saved when collecting this message, or it may be from a different group.'
        //                     );
        //                   }
        //                   throw new Error(`HTTP error! status: ${originalRes.status}`);
        //                 }
        //                 return originalRes.json();
        //               });
        //             }

        //             if (res.status === 404) {
        //               throw new Error(
        //                 'Attachment file not found. The attachment might not have been saved when collecting this message, or it may be from a different group.'
        //               );
        //             }
        //             throw new Error(`HTTP error! status: ${res.status}`);
        //           }
        //           return res.json();
        //         })
        //         .then((data) => {
        //           try {
        //             let messagesArray: any[] = [];
        //             if (Array.isArray(data)) {
        //               messagesArray = data;
        //             } else if (data && typeof data === 'object' && Array.isArray(data.messages)) {
        //               messagesArray = data.messages;
        //             }

        //             if (messagesArray.length > 0) {
        //               attachmentCacheRef.current.set(cacheKey, messagesArray);
        //               onOpenMergedForward(messagesArray);
        //             } else {
        //               toast({ intent: 'info', msg: i18n.t('Chatroom.NoMessagesFound') || 'No messages found' });
        //             }
        //           } catch (error) {
        //             console.error('Error parsing fetched messages:', error);
        //             toast({ intent: 'error', msg: i18n.t('Chatroom.LoadMessagesFailed') || 'Failed to load messages' });
        //           }
        //         })
        //         .catch((error) => {
        //           let errorMsg: string;
        //           if (isOldFormat && error.message?.includes('not found')) {
        //             errorMsg =
        //               i18n.t('Chatroom.OldCollectionMessageAttachmentNotFound') ||
        //               'This is an old collected message. The attachment file may have been deleted. Please re-collect this message to solidify the content.';
        //           } else if (error.message?.includes('not found')) {
        //             errorMsg =
        //               i18n.t('Chatroom.AttachmentNotFound') ||
        //               'Attachment not found. The attachment might not have been saved properly when collecting, or it may be from a different group.';
        //           } else {
        //             errorMsg = i18n.t('Chatroom.LoadMessagesFailed') || 'Failed to load messages';
        //           }
        //           toast({ intent: 'error', msg: errorMsg });
        //         });
        //     } else {
        //       toast({ intent: 'info', msg: i18n.t('Chatroom.NoMessagesAvailable') || 'No messages available' });
        //     }
        //   }
        //   return;
        // }

        try {
          if (msgAny.attachment) {
            const attachmentUrl = getAttachmentUrl(msgAny.attachment);
            if (attachmentUrl) {
              validateAndOpenUrl(attachmentUrl, 'Chatroom.urlInvalid');
              return;
            }
          }
        } catch (error) {
          console.error('Error handling attachment:', error);
        }

        try {
          if (msgAny.audio) {
            playAudio(msgAny, (errorMsg) => toast({ intent: 'error', msg: errorMsg }));
            return;
          }
        } catch (error) {
          console.error('Error playing audio:', error);
        }

        try {
          if (msgAny.card) {
            setShowAcceptCard(msgAny.card);
          }
        } catch (error) {
          console.error('Error showing contact card:', error);
        }
      } catch (error) {
        console.error('Error in onPress handler:', error);
        toast({
          intent: 'error',
          msg: i18n.t('Chatroom.HandleFailed') || 'An error occurred while processing this message'
        });
      }
    },
    [validateAndOpenUrl, playAudio, toast, setShowAcceptCard] // 暂时隐藏转发功能，移除 onOpenMergedForward
  );

  const onUrlPress = async (url: string) => {
    try {
      const urlObj = new URL(url);

      const rootDomain = urlObj.hostname.split('.').slice(-2).join('.');
      if (legitHosts.includes(rootDomain)) {
        if (!urlObj.pathname.startsWith('/launch')) {
          window.open(url, '_blank', 'noopener,noreferrer');
          return;
        }
        const params = urlObj.searchParams;
        const code = params.get('code') || '';
        const result = await _dataProvider.launchUrl(code);
        if (!result || typeof result !== 'object') {
          return;
        }
        if (result?.cmd === 'joinStudyGroup') {
          if (!result.data || typeof result.data !== 'object') {
            return;
          }
          if (groupId.toString() !== result?.data?.value) {
            const msg = result?.data?.status ? 'Chatroom.JoinSuccess' : 'Chatroom.JoinWaiting';
            toast({ intent: 'info', msg: i18n.t(msg) });
            window.location.reload();
          }
        }
      } else {
        validateAndOpenUrl(url, 'Chatroom.urlInvalid');
      }
    } catch (_error) {
      toast({ intent: 'error', msg: i18n.t('Chatroom.urlInvalid') });
    }
  };

  const toggleSearch = () => {
    setShowSearchInput(!showSearchInput);
    if (!showSearchInput) {
      clearSearchState();
      setFilterState({
        activeFilter: 'none',
        filteredMessages: messages
      });
    } else {
      clearSearchState();
    }
  };

  const pickFile2Download = (groupId: number, fileId: number): void => {
    const remoteFile = _dataProvider.getFileUrlInChat(groupId, fileId);
    if (!remoteFile || typeof remoteFile !== 'string') {
      return;
    }
    validateAndOpenUrl(remoteFile, 'Chatroom.urlInvalid');
  };

  useEffect(() => {
    if (showEmojiPicker) {
      const timer = setTimeout(() => {
        const searchInput = document.querySelector('.emoji-picker-container input[type="text"]') as HTMLInputElement;
        if (searchInput) {
          searchInput.placeholder =
            i18n.language === 'chs' ? '搜索表情' : i18n.language === 'cht' ? '搜尋表情' : i18n.t('emoji.search');

          const handleInput = (e: Event) => {
            const value = (e.target as HTMLInputElement).value;

            if (/[\u4e00-\u9fa5]/.test(value)) {
              const results = searchChineseEmojis(value);
              setChineseSearchResults(results);
              setShowChineseResults(results.length > 0);

              const emojiBody = document.querySelector('.epr-body');
              if (emojiBody) {
                (emojiBody as HTMLElement).style.display = results.length > 0 ? 'none' : '';
              }
            } else {
              setShowChineseResults(false);
              setChineseSearchResults([]);
              const emojiBody = document.querySelector('.epr-body');
              if (emojiBody) {
                (emojiBody as HTMLElement).style.display = '';
              }
            }
          };
          searchInput.addEventListener('input', handleInput);
        }
      }, 100);

      return () => {
        clearTimeout(timer);
      };
    } else {
      setShowChineseResults(false);
      setChineseSearchResults([]);
    }
  }, [showEmojiPicker, setChineseSearchResults, setShowChineseResults]);

  const uploadAction = () => {
    const inputF = document.createElement('input');
    inputF.setAttribute('type', 'file');
    inputF.setAttribute('accept', '*.*');
    inputF.setAttribute('multiple', 'multiple');

    inputF.onchange = async (e) => {
      const tg = e?.target as HTMLInputElement | null;
      const fileList = Array.from(tg?.files || []);
      if (fileList.length === 0) {
        return;
      }
      const result = await uploadFilesToChat(fileList);
      if (result) {
        showUploadResult(result.successCount, result.failedCount, fileList.length);
      }
    };
    inputF.click();
  };

  const handleEmojiSelect = useCallback((emoji: string) => {
    setInputText((prev) => prev + emoji);
    setShowEmojiPicker(false);

    setTimeout(() => {
      giftedChatRef?.current?.focusTextInput();
    }, 100);
  }, []);

  const toggleEmojiPicker = useCallback(() => {
    setShowEmojiPicker((prev) => !prev);
  }, []);

  const { renderIcon: renderEmojiIcon, renderHoverIcon: renderEmojiHoverIcon } = createIconPair({
    icon: emojiIcon,
    title: i18n.t('emoji.addEmoji'),
    useHoverBackground: true
  });

  const { renderIcon: renderActionIcon, renderHoverIcon: renderActionHoverIcon } = createIconPair({
    icon: uploadIcon,
    title: i18n.t('uploadDocument'),
    useHoverBackground: true
  });

  const { renderIcon: renderSendIcon, renderHoverIcon: renderSendHoverIcon } = createIconPair({
    icon: sendIcon,
    alt: 'Send',
    useHoverBackground: true
  });

  const { renderIcon: renderVoiceIcon, renderHoverIcon: renderVoiceHoverIcon } = createIconPair({
    icon: voiceIcon,
    title: i18n.t('voiceInput'),
    useHoverBackground: true
  });

  const { renderIcon: renderKeyboardIcon, renderHoverIcon: renderKeyboardHoverIcon } = createIconPair({
    icon: keyboardIcon,
    title: i18n.t('keyboardInput'),
    useHoverBackground: true
  });

  const onPressAvatar = (user: any) => {
    const targetUserId = user?.id ?? user?.userId;
    if (!canOpenUserProfile(targetUserId)) {
      return;
    }
    setShowMessageSender({
      ...user,
      id: targetUserId,
      tag: _dataProvider.getMyTagForUser(targetUserId),
      block: _dataProvider.getMyBlockForUser(targetUserId)
    });
  };

  const renderCustomView = useCallback(
    (msg: any) => {
      const showDownloadPrompt = (event: React.MouseEvent) => {
        event.stopPropagation();
        showAlertDialog('此类消息需要在小鸽子（iDigest App）中查看和操作，请下载或打开小鸽子完成支持。');
      };

      const renderGroupTaskIcon = (kind: GroupTaskCard['kind']) => {
        const icon = groupTaskIcons[kind];
        return (
          <span className={`webchat-task-card-icon${icon ? ' webchat-task-card-icon-with-image' : ''}`}>
            {icon ? <img src={icon} alt='' className='webchat-task-card-icon-img' /> : groupTaskIconText[kind]}
          </span>
        );
      };

      const renderGroupTaskCard = (card: GroupTaskCard) => {
        const label = groupTaskLabels[card.kind];
        const deadline = formatGroupTaskDeadline(card.deadline);
        const remainingTime = formatRemainingSeconds(card.timeRemaining);
        const progress = Math.min(1, Math.max(0, Number(card.progress ?? 0)));
        const attendance = getDisplayAttendance(card.attendance);

        if (card.kind === 'readingPlan') {
          return (
            <button type='button' className='webchat-task-card webchat-task-card-plan' onClick={showDownloadPrompt}>
              {card.image && <img className='webchat-task-card-cover' src={card.image} alt='' />}
              <span className='webchat-task-card-plan-copy'>
                <span className='webchat-task-card-title'>{card.title || label}</span>
                {card.description && <span className='webchat-task-card-desc'>{card.description}</span>}
              </span>
            </button>
          );
        }

        if (card.kind === 'checkinSuccess') {
          return (
            <button type='button' className='webchat-task-card webchat-task-card-success' onClick={showDownloadPrompt}>
              <span className='webchat-task-card-success-quote'>
                <span className='webchat-task-card-row webchat-task-card-first-row'>
                  {renderGroupTaskIcon(card.kind)}
                  <span>
                    {label} {card.id && <small>#{card.id}</small>}
                  </span>
                </span>
                {progress > 0 && (
                  <span className='webchat-task-card-row'>打卡进展：{(progress * 100).toFixed(0)}%</span>
                )}
                {remainingTime && <span className='webchat-task-card-row'>剩余时间：{remainingTime}</span>}
              </span>
              {card.description && <span className='webchat-task-card-success-message'>{card.description}</span>}
            </button>
          );
        }

        return (
          <button
            type='button'
            className={`webchat-task-card webchat-task-card-${card.kind}`}
            onClick={showDownloadPrompt}
          >
            <span className='webchat-task-card-row webchat-task-card-first-row'>
              {renderGroupTaskIcon(card.kind)}
              <span>
                {label} {card.id && <small>#{card.id}</small>}
              </span>
            </span>
            {deadline && <span className='webchat-task-card-row'>期限：{deadline}</span>}
            {card.scriptures && card.scriptures.length > 0 && (
              <span className='webchat-task-card-row'>
                阅读经文：
                <strong>{card.scriptures.slice(0, 5).join('，')}</strong>
              </span>
            )}
            {card.description && (
              <span className='webchat-task-card-row webchat-task-card-desc'>{card.description}</span>
            )}
            {attendance.length > 0 && (
              <span className='webchat-task-card-attendance'>
                {attendance.map((item, index) =>
                  item.displayIndex === -1 ? (
                    <span key={`ellipsis-${index}`} className='webchat-task-card-muted'>
                      ...
                    </span>
                  ) : (
                    <span key={`${item.displayIndex}-${item.name}`}>
                      {item.displayIndex + 1}. {item.name}
                    </span>
                  )
                )}
              </span>
            )}
          </button>
        );
      };

      // 暂时隐藏转发功能 - 合并转发消息渲染
      // const msgAny = msg as any;
      // if (isMergedForwardMessage(msgAny)) {
      //   const title = i18n.t('Chatroom.ChatHistory');
      //   const mergedMessages = extractForwardedMessages(msgAny);

      //   const hasMessages = mergedMessages && Array.isArray(mergedMessages) && mergedMessages.length > 0;
      //   const previewContent = msgAny.mergedForwardContent || '';
      //   const hasPreviewContent = previewContent && previewContent.trim().length > 0;
      //   const isLoading =
      //     (msgAny.mergedForwardAttachmentUrl ||
      //       (msgAny.attachment && typeof msgAny.attachment === 'object' && (msgAny.attachment as any).url)) &&
      //     !hasMessages &&
      //     !hasPreviewContent &&
      //     !loadingMessagesRef.current.has(msgAny.id);

      //   const previewLines = previewContent ? previewContent.split('\n') : [];

      //   const titleHeight = '20px';
      //   const previewLineHeight = '18px';
      //   const minPreviewHeight = previewLines.length > 0 ? `${Math.max(2, previewLines.length) * 18}px` : '36px';

      //   return (
      //     <div
      //       onClick={(e) => {
      //         e.stopPropagation();
      //         if (onPress) {
      //           onPress(msgAny);
      //         }
      //       }}
      //       style={{
      //         marginTop: '5px',
      //         marginBottom: '5px',
      //         marginLeft: '15px',
      //         marginRight: '15px',
      //         cursor: 'pointer',
      //         color: '#333',
      //         display: 'flex',
      //         flexDirection: 'column',
      //         alignItems: 'flex-start',
      //         minHeight: `calc(${titleHeight} + ${minPreviewHeight} + 10px)`
      //       }}>
      //       <div
      //         style={{
      //           fontSize: '14px',
      //           fontWeight: 'bold',
      //           marginBottom: '10px',
      //           color: '#000',
      //           minHeight: titleHeight,
      //           lineHeight: titleHeight,
      //           width: '100%'
      //         }}>
      //         {title}
      //       </div>

      //       <div
      //         style={{
      //           minHeight: minPreviewHeight,
      //           width: '100%',
      //           display: 'flex',
      //           flexDirection: 'column',
      //           justifyContent: 'flex-start'
      //         }}>
      //         {isLoading ? (
      //           <>
      //             {[0, 1].map((index) => (
      //               <div
      //                 key={index}
      //                 style={{
      //                   height: previewLineHeight,
      //                   marginBottom: index === 0 ? '6px' : '0',
      //                   backgroundColor: '#f0f0f0',
      //                   borderRadius: '4px',
      //                   width: '100%',
      //                   animation: 'pulse 1.5s ease-in-out infinite'
      //                 }}
      //               />
      //             ))}
      //             <style>
      //               {`
      //               @keyframes pulse {
      //                 0%, 100% {
      //                   opacity: 1;
      //                 }
      //                 50% {
      //                   opacity: 0.5;
      //                 }
      //               }
      //             `}
      //             </style>
      //           </>
      //         ) : previewLines.length > 0 ? (
      //           previewLines.map((line: string, index: number) => (
      //             <div
      //               key={index}
      //               style={{
      //                 minHeight: previewLineHeight,
      //                 marginBottom: index < previewLines.length - 1 ? '6px' : '0',
      //                 fontSize: '13px',
      //                 color: '#666',
      //                 textAlign: 'left',
      //                 width: '100%',
      //                 maxWidth: '100%',
      //                 fontWeight: 600,
      //                 lineHeight: previewLineHeight,
      //                 wordBreak: 'break-word',
      //                 overflowWrap: 'break-word',
      //                 overflow: 'hidden',
      //                 textOverflow: 'ellipsis',
      //                 whiteSpace: 'nowrap'
      //               }}>
      //               {line}
      //             </div>
      //           ))
      //         ) : (
      //           msgAny.mergedForwardContent && (
      //             <div
      //               style={{
      //                 minHeight: previewLineHeight,
      //                 fontSize: '13px',
      //                 color: '#666',
      //                 textAlign: 'left',
      //                 width: '100%',
      //                 maxWidth: '100%',
      //                 fontWeight: 600,
      //                 lineHeight: previewLineHeight,
      //                 wordBreak: 'break-word',
      //                 overflowWrap: 'break-word',
      //                 overflow: 'hidden'
      //               }}>
      //               {msgAny.mergedForwardContent
      //                 .replace(/\b音频\b/g, i18n.t('Chatroom.AudioMessage'))
      //                 .replace(/\[音频\]/g, `[${i18n.t('Chatroom.AudioMessage')}]`)}
      //             </div>
      //           )
      //         )}
      //       </div>
      //     </div>
      //   );
      // }

      if (msg.groupTaskCard) {
        return renderGroupTaskCard(msg.groupTaskCard);
      }

      if (msg.card) {
        const contactDisplayName = msg.card[1] || '';

        return (
          <div style={{ padding: 14, paddingBottom: 0 }}>
            <div style={{ flexDirection: 'row', padding: 0 }}>
              <img src={defaultAvatar} alt='' style={{ verticalAlign: 'middle', width: 60, height: 60 }} />
              <span style={{ paddingLeft: 10 }}>{contactDisplayName}</span>
            </div>
            <p
              style={{
                width: 64,
                fontSize: 12,
                textAlign: 'center',
                color: 'var(--idigest-chat-muted, #707070)'
              }}
            >
              {i18n.t('Chatroom.ContactCard')}
            </p>
          </div>
        );
      }
      return null;
    },
    [showAlertDialog]
  );

  const renderMessageWithPreCompletedNotice = useCallback((props: any) => {
    const notice = props.currentMessage?.preCompletedNotice;
    if (!notice) {
      return <Message {...props} />;
    }

    const title = notice.title || i18n.t('Chatroom.CheckInNumber', { id: notice.checkInId });
    return (
      <div className='webchat-message-with-pre-completed-notice'>
        <Message {...props} />
        <div className='webchat-pre-completed-notice' data-testid='check-in-pre-completed-notice'>
          {notice.users.map((user: { userId: string; displayName: string | null }) => (
            <div key={user.userId} className='webchat-pre-completed-notice-row'>
              {i18n.t('Chatroom.PreCompletedNotice', {
                name: user.displayName || i18n.t('Chatroom.AnonymousUser'),
                title
              })}
            </div>
          ))}
        </div>
      </div>
    );
  }, []);
  const searchBoxStyles: Partial<ISearchBoxStyles> = useMemo(() => getSearchBoxStyles(theme), [theme]);

  return (
    <div
      id='idEnclosingParent'
      className='webchat-container'
      style={{
        height: chatHeight,
        backgroundColor: 'var(--idigest-chat-bg, #ffffff)',
        color: 'var(--idigest-chat-fg, #171717)'
      }}
    >
      {renderToaster()}
      <ConfirmDialog
        show={confirmDialog.show}
        title={confirmDialog.title}
        content={confirmDialog.content}
        onConfirm={confirmDialog.onConfirm}
        onCancel={confirmDialog.onCancel || (() => setConfirmDialog((prev) => ({ ...prev, show: false })))}
        confirmText={i18n.t('Confirm')}
        cancelText={i18n.t('Cancel')}
      />
      <ConfirmDialog
        show={alertDialog.show}
        content={alertDialog.content}
        onClose={() => setAlertDialog((prev) => ({ ...prev, show: false }))}
        showConfirm={false}
        showCancel={false}
        closeText='OK'
      />
      <div id='idGroupActionButton' className='fellowshipActionDiv'>
        <div className='fellowshipActionTags'>
          <button
            className={`fellowshipAction ${filterState.activeFilter === 'user' ? 'activeButton' : ''}`}
            title={i18n.t('Chatroom.MyCollection')}
            onClick={() => applyFilter('user')}
          >
            {i18n.t('Chatroom.MyCollectionFilter')}
          </button>
          <button
            className={`fellowshipAction ${filterState.activeFilter === 'group' ? 'activeButton' : ''}`}
            title={i18n.t('Chatroom.GroupCollection')}
            onClick={() => applyFilter('group')}
          >
            {i18n.t('Chatroom.GroupCollectionFilter')}
          </button>
          <button
            className={`fellowshipAction ${filterState.activeFilter === 'file' ? 'activeButton' : ''}`}
            onClick={() => applyFilter('file')}
          >
            {i18n.t('document')}
          </button>
          <button className='fellowshipAction' onClick={() => setPickFile(true)}>
            {i18n.t('downloadDocument')}
          </button>
        </div>
        <button className={`search-button ${showSearchInput ? 'active' : ''}`} onClick={toggleSearch}>
          <img src={searchIcon} alt='Search' className='search-icon-default' />
          <img src={searchActiveIcon} alt='Search' className='search-icon-active' />
        </button>
      </div>
      {showSearchInput && (
        <div className='search-input-container'>
          <SearchBoxChrome>
            <SearchBox
              styles={searchBoxStyles}
              placeholder={i18n.t('Search')}
              onChange={handleSearchChange}
              onClear={clearSearchState}
            />
          </SearchBoxChrome>
        </div>
      )}
      <div className='giftedchat-container'>
        <GiftedChat
          renderMessageList={
            getIdigestChatHostConfig().renderMessageList
              ? (props: any) =>
                  getIdigestChatHostConfig().renderMessageList!({
                    ...props,
                    onSelectionChange: (active: boolean, ids: number[]) => {
                      setIsBatchSelectMode(active);
                      setSelectedMessageIds(new Set(ids));
                    },
                    firstUnreadMessageId,
                    onUnreadVisible: dismissUnreadIndicator,
                    onLoadEarlier: () => loadEarlierMessagesAsync(searchState.inSearch),
                    canLoadEarlier:
                      getCurrentMessages().length > 0 &&
                      (searchState.inSearch ? scrollFromId : fromId) !== getCurrentMessages()[0]?.id
                  })
              : undefined
          }
          messageContextKey={`${groupId}:${filterState.activeFilter}:${searchState.inSearch}`}
          giftedChatRef={giftedChatRef}
          onPaste={handlePaste}
          text={inputText}
          messages={getCurrentMessages()}
          onSend={onSend}
          user={{
            id: getJwtObj()?.userId
          }}
          playingAudioId={playingAudioId}
          renderUsernameOnMessage={discussionShareInGroup !== undefined}
          hideFooterMetadata={discussionShareInGroup !== undefined}
          renderCustomView={renderCustomView}
          renderMessage={renderMessageWithPreCompletedNotice}
          placeholder={
            currentUserCannotPost
              ? i18n.t('Chatroom.MemberCannotPostInGroup')
              : isRecording
                ? `${i18n.t('Chatroom.recordingPlaceholder', {
                    duration: formatDuration(recordingDuration)
                  })}，${i18n.t('Chatroom.releaseToSend')}`
                : isVoiceMode
                  ? i18n.t('Chatroom.voiceModePlaceholder')
                  : i18n.t('Chatroom.inputPlaceHolder')
          }
          isVoiceMode={isVoiceMode}
          isRecording={isRecording}
          isInputDisabled={currentUserCannotPost}
          onPressVoiceButton={toggleVoiceMode}
          renderVoiceIcon={renderVoiceIcon}
          renderVoiceHoverIcon={renderVoiceHoverIcon}
          renderVoiceLabel={i18n.t('voiceInput')}
          onStartRecording={startRecording}
          onStopRecording={stopRecording}
          renderKeyboardIcon={renderKeyboardIcon}
          renderKeyboardHoverIcon={renderKeyboardHoverIcon}
          renderKeyboardLabel={i18n.t('keyboardInput')}
          listViewProps={{
            onScroll: handleScroll,
            onUserScroll: markUnreadHistoryScrolled,
            scrollContainerRef,
            shouldAutoScrollToBottom: !isLoadingEarlier,
            isLoadingEarlier: isLoadingEarlier
          }}
          onInputTextChanged={onInputTextChanged}
          onQuotePress={handleQuotePress}
          messageActionSheet={messageActionSheet}
          onUrlPress={onUrlPress}
          onPress={onPress}
          onPressAvatar={onPressAvatar}
          isBatchSelectMode={isBatchSelectMode}
          selectedMessageIds={selectedMessageIds}
          toggleMessageSelection={toggleMessageSelection}
          onPressActionButton={uploadAction}
          renderActionIcon={renderActionIcon}
          renderActionLabel={i18n.t('uploadDocument')}
          renderBubble={null}
          renderActionHoverIcon={renderActionHoverIcon}
          onPressEmojiButton={toggleEmojiPicker}
          renderEmojiIcon={renderEmojiIcon}
          renderEmojiHoverIcon={renderEmojiHoverIcon}
          renderEmojiLabel={i18n.t('emoji.addEmoji')}
          renderSendIcon={renderSendIcon}
          renderSendHoverIcon={renderSendHoverIcon}
          renderSendLabel={i18n.t('Chatroom.send')}
          alwaysShowSend={true}
          textInputProps={
            currentUserCannotPost
              ? {
                  disabled: true,
                  readOnly: true,
                  className: 'leader-only-disabled-composer',
                  'aria-disabled': true
                }
              : undefined
          }
          label={<div style={{ whiteSpace: 'nowrap' }}>{i18n.t('Chatroom.send')}</div>}
          renderAboveInputToolbar={
            uploadProgress
              ? () => (
                  <div className='upload-progress-bar-container'>
                    <div className='upload-progress-bar-label'>
                      <span className='upload-progress-bar-filename' title={uploadProgress.fileName}>
                        {uploadProgress.totalFiles > 1
                          ? i18n.t('Chatroom.UploadProgressCount', {
                              index: uploadProgress.fileIndex,
                              total: uploadProgress.totalFiles
                            })
                          : ''}
                        {uploadProgress.fileName}
                      </span>
                      <span className='upload-progress-bar-percent'>{uploadProgress.percent}%</span>
                    </div>
                    <div className='upload-progress-bar-track'>
                      <div className='upload-progress-bar-fill' style={{ width: `${uploadProgress.percent}%` }} />
                    </div>
                  </div>
                )
              : undefined
          }
        />

        {!showSearchInput && showUnreadIndicator && unreadCount >= UNREAD_INDICATOR_MIN_COUNT && (
          <button
            type='button'
            className='unread-message-indicator'
            aria-label={i18n.t('Chatroom.UnreadMessages', { count: unreadCount })}
            disabled={firstUnreadMessageId === null}
            onClick={scrollToFirstUnreadMessage}
          >
            <img src={upIcon} alt='' />
            <span>{i18n.t('Chatroom.UnreadMessages', { count: unreadCount })}</span>
          </button>
        )}

        {showEmojiPicker && (
          <Suspense fallback={null}>
            <EmojiPickerWrapper
              groupId={groupId}
              onEmojiSelect={handleEmojiSelect}
              onClose={() => setShowEmojiPicker(false)}
              position={{ bottom: '65px', left: '70px' }}
              chineseSearchResults={chineseSearchResults}
              showChineseResults={showChineseResults}
              onChineseResultClick={(emoji) => {
                handleEmojiSelect(emoji);
                setShowChineseResults(false);
                setChineseSearchResults([]);
              }}
              onCloseChineseResults={() => {
                setShowChineseResults(false);
                setChineseSearchResults([]);
              }}
              onChineseSearch={(value) => {
                const results = searchChineseEmojis(value);
                setChineseSearchResults(results);
                setShowChineseResults(results.length > 0);
              }}
            />
          </Suspense>
        )}
      </div>
      {pickFile && (
        <IPromptPickFilesInGroup
          groupId={groupId}
          processFile={async (groupId: number, fileId: number) => pickFile2Download(groupId, fileId)}
          onDismiss={() => setPickFile(false)}
        />
      )}
      {showUserSelection && (
        <Dialog
          hidden={false}
          onDismiss={() => setShowUserSelection(false)}
          dialogContentProps={{
            showCloseButton: true
          }}
        >
          <div className='member-searchbox-wrapper'>
            <SearchBoxChrome>
              <SearchBox
                styles={searchBoxStyles}
                placeholder={i18n.t('Search')}
                autoFocus={!isMobile}
                value={userSelectionQuery}
                onChange={(_, value) => setUserSelectionQuery(value || '')}
                onClear={() => setUserSelectionQuery('')}
              />
            </SearchBoxChrome>
          </div>
          <div className='member-container'>
            {[theAllUserUser, ...(groupInfo.users ?? [])]
              .filter((user: IUser) => {
                const query = userSelectionQuery.trim().toLowerCase();
                return !query || [user.name, user.displayName].some((name) => name?.toLowerCase().includes(query));
              })
              .map(renderUserInSelection)}
          </div>
        </Dialog>
      )}
      {showMessageSender && (
        <UserProfileDialog
          user={showMessageSender}
          dismiss={setShowMessageSender}
          launchOneOnOneWithFriend={launchOneOnOneWithFriend}
        />
      )}
      {showAcceptCard && (
        <AcceptContactCard
          userCard={showAcceptCard}
          dismiss={setShowAcceptCard}
          launchOneOnOneWithFriend={launchOneOnOneWithFriend}
        />
      )}
      {/* 暂时隐藏转发功能 */}
      {/* {showShareMethodDialog && (
        <div className='delete-confirm-overlay' onClick={() => setShowShareMethodDialog(false)}>
          <div className='delete-confirm-dialog' onClick={(e) => e.stopPropagation()}>
            <div className='delete-confirm-title'>{i18n.t('Chatroom.SelectShareMethod')}</div>
            <div className='delete-confirm-content share-method-dialog-content'></div>
            <div className='share-method-dialog-buttons'>
              <button className='share-method-dialog-button' onClick={() => handleShareMethod('one-by-one')}>
                {i18n.t('Chatroom.ShareOneByOne')}
              </button>
              <button
                className={`share-method-dialog-button ${hasMergedForwardMessage() ? 'disabled' : ''}`}
                onClick={() => handleShareMethod('package')}
                disabled={hasMergedForwardMessage()}>
                {i18n.t('Chatroom.SharePackage')}
              </button>
            </div>
          </div>
        </div>
      )} */}
    </div>
  );
};
